import {
  EMAIL_CAMPAIGN_SAVE_CONFLICT_CODES,
  EMAIL_FLOW_SAVE_CONFLICT_CODES,
} from './email-admin.js'
import { ACTIVE_CONTEXT_FORBIDDEN_ERROR } from './organization.js'
import type { GoogleDriveConnectionErrorCode } from './google-drive-link.js'
import type { TranscriptionFailureCode } from './transcription-failure.js'

/**
 * Google Drive connection answers the web upload dialog turns into a next step
 * (authorize again, tick the permission, "import unavailable").
 */
const GOOGLE_DRIVE_NOT_CONNECTED =
  'google_drive_not_connected' satisfies GoogleDriveConnectionErrorCode
const GOOGLE_DRIVE_SCOPE_MISSING =
  'google_drive_scope_missing' satisfies GoogleDriveConnectionErrorCode
const GOOGLE_DRIVE_UNAVAILABLE = 'google_drive_unavailable' satisfies GoogleDriveConnectionErrorCode

/** An empty credit pool on a transcription retry; the toast names the balance. */
const INSUFFICIENT_CREDITS = 'insufficient_credits' satisfies TranscriptionFailureCode

/**
 * API error responses (HTTP status → `{ error }` codes) that the clients
 * already turn into an ordinary screen: credits run out, no running campaign, a dead invite, an
 * already-claimed promotion, an autosave version conflict, a daily trial cap,
 * a self-healing active context, a Google Drive authorization to redo. Error
 * reporting records these as context instead of as failures.
 *
 * A pair counts only when BOTH the status and the code match. Everything not
 * listed stays a failure on purpose: 400 and 422 usually mean the client and
 * the API disagree on the request, a 404 without a code means a route the
 * client should not have called, and 5xx is the server's own fault.
 */
export const EXPECTED_CLIENT_ERROR_OUTCOMES = {
  // Credits ran out before a transcription retry or a meeting-notes run.
  402: [INSUFFICIENT_CREDITS, 'insufficient_allowance'],
  403: [ACTIVE_CONTEXT_FORBIDDEN_ERROR],
  404: [
    'promotion_unavailable',
    'campaign_not_found',
    'invalid_invite',
    'invalid_promotion_invite',
    'not_found',
  ],
  409: [
    'promotion_already_claimed',
    'survey_edit_version_conflict',
    GOOGLE_DRIVE_NOT_CONNECTED,
    ...EMAIL_FLOW_SAVE_CONFLICT_CODES,
    ...EMAIL_CAMPAIGN_SAVE_CONFLICT_CODES,
  ],
  410: ['invite_expired', 'expired'],
  422: [GOOGLE_DRIVE_SCOPE_MISSING],
  429: ['rate_limited'],
  503: [GOOGLE_DRIVE_UNAVAILABLE],
} as const satisfies Readonly<Record<number, readonly string[]>>

const OUTCOMES: ReadonlyMap<number, ReadonlySet<string>> = new Map(
  Object.entries(EXPECTED_CLIENT_ERROR_OUTCOMES).map(([status, codes]) => [
    Number(status),
    new Set<string>(codes),
  ])
)

/** Whether `(status, code)` is one of {@link EXPECTED_CLIENT_ERROR_OUTCOMES}. */
export function isExpectedClientErrorOutcome(status: number, code: string | null): boolean {
  if (code === null) return false
  return OUTCOMES.get(status)?.has(code) ?? false
}
